if (!customElements.get('excel-wire-order')) {
  // Material 3 motion for the splice card: emphasized decelerate to enter, emphasized accelerate to
  // leave. Exits hold their last frame until the element is hidden, so nothing flashes back.
  const wireMotionEnter = 'cubic-bezier(.05, .7, .1, 1)';
  const wireMotionExit = 'cubic-bezier(.3, 0, .8, .15)';
  const wireReducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function wireMotion(element, keyframes, options) {
    if (!element || !element.animate || wireReducedMotion?.matches) return Promise.resolve(null);
    const animation = element.animate(keyframes, options);
    return animation.finished.then(() => animation, () => null);
  }

  customElements.define('excel-wire-order', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.byFoot = this.dataset.byFoot === 'true';
      this.reelLength = Number(this.dataset.reelLength);
      this.length = Number(this.querySelector('[data-wire-feet]').value);
      this.custom = this.querySelector('[data-wire-custom]').getAttribute('aria-pressed') === 'true';
      this.initialLength = this.length;
      this.initialCustom = this.custom;
      this.form = this.querySelector('.exd-wire-form');
      this.feet = this.querySelector('[data-wire-feet]');
      this.cuts = this.querySelector('[data-wire-cuts]');
      this.quantity = this.querySelector('[data-wire-cart-quantity]');
      // Only trade accounts get Add to Cart (and a cart form); everyone else gets Sign in for wiring in its place.
      this.add = this.querySelector('[data-wire-add]');
      this.trade = this.querySelector('[data-wire-trade]');
      this.quote = this.querySelector('[data-wire-quote]');
      this.message = this.querySelector('[data-wire-message]');
      this.presets = [...this.querySelectorAll('[data-wire-length]')];
      this.customButton = this.querySelector('[data-wire-custom]');
      this.customField = this.querySelector('.exd-wire-custom-field');
      this.priceBreaks = JSON.parse(this.querySelector('[data-wire-price-breaks]').textContent);
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style:'currency', currency:this.dataset.currency });
      this.number = new Intl.NumberFormat(document.documentElement.lang || 'en-US');
      this.controller = new AbortController();
      const options = {signal:this.controller.signal};
      this.presets.forEach(button => button.addEventListener('click', () => {
        this.length = Number(button.dataset.wireLength);
        this.feet.value = this.length;
        this.custom = false;
        this.update();
      }, options));
      this.customButton.addEventListener('click', () => {
        this.custom = true;
        this.update();
        this.feet.focus({preventScroll:true});
        this.feet.select();
      }, options);
      this.feet.addEventListener('input', () => { this.length = this.feet.valueAsNumber; this.update(); }, options);
      this.feet.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        const preset = this.presets.find(button => Number(button.dataset.wireLength) === this.length) || this.presets[0];
        preset.click();
        preset.focus();
      }, options);
      this.cuts.addEventListener('input', () => this.update(), options);
      this.cuts.addEventListener('change', () => this.update(), options);
      this.splicing = false;
      this.splice = this.querySelector('[data-wire-splice]');
      if (this.splice) this.setupSplice(options);
      this.form.addEventListener('submit', event => {
        this.update();
        if (!this.add || this.add.disabled || this.add.hidden) {
          event.preventDefault();
          if (this.feet.getAttribute('aria-invalid') === 'true') this.feet.focus();
          else if (this.cuts.getAttribute('aria-invalid') === 'true') this.cuts.focus();
          return;
        }
        // The cart popup normally sends spliced cuts as separate lines. Without it, add them here and open the cart.
        if (!this.form.dataset.cartLines || !window.fetch) return;
        event.preventDefault();
        this.add.disabled = true;
        fetch(`${this.form.getAttribute('action').replace(/\/$/, '')}.js`, {
          method:'POST',
          credentials:'same-origin',
          headers:{'Content-Type':'application/json', Accept:'application/json'},
          body:JSON.stringify({items:JSON.parse(this.form.dataset.cartLines)})
        }).then(response => {
          if (!response.ok) throw new Error('Spliced cuts were not added');
          window.location.assign(this.dataset.cartUrl || '/cart');
        }).catch(() => { this.add.disabled = this.addDisabled; });
      }, options);
      this.update();
      if (this.add) {
        this.observer = new MutationObserver(() => {
          if (this.add.getAttribute('aria-busy') !== 'true' && this.add.disabled !== this.addDisabled) this.add.disabled = this.addDisabled;
        });
        this.observer.observe(this.add, {attributes:true, attributeFilter:['disabled']});
      }
      this.stockKey = this.stockState();
      this.stockObserver = new MutationObserver(() => {
        const state = this.stockState();
        if (state === this.stockKey) return;
        this.stockKey = state;
        this.update();
      });
      this.stockObserver.observe(this.quantity, {attributes:true, attributeFilter:['min','max','step','disabled']});
    }

    // Splicing: the Splice icon beside Custom opens a card of cut fields in place of the lengths and
    // the number of cuts. All cuts are priced together; the card's Add to Cart adds each as its own
    // cart line, then resets the order.
    setupSplice(options) {
      this.spliceToggle = this.querySelector('[data-wire-splice-toggle]');
      this.spliceCuts = this.splice.querySelector('[data-wire-splice-cuts]');
      this.spliceAdd = this.splice.querySelector('[data-wire-splice-add]');
      this.spliceCart = this.splice.querySelector('[data-wire-splice-cart]');
      this.spliceCartLabel = this.splice.querySelector('[data-wire-splice-cart-label]');
      this.spliceSummary = this.splice.querySelector('[data-wire-splice-summary]');
      this.lengthGroup = this.querySelector('.exd-wire-lengths');
      this.maxCuts = Number(this.splice.dataset.maxCuts) || 25;
      this.spliceGroup = this.newSpliceGroup();
      this.spliceMoving = false;
      this.spliceToggle.addEventListener('click', event => this.openSplice(event), options);
      this.splice.querySelector('[data-wire-splice-close]').addEventListener('click', () => this.closeSplice(), options);
      this.spliceCart.addEventListener('click', () => this.addSplice(), options);
      this.spliceAdd.addEventListener('click', () => {
        if (this.cutInputs().length >= this.maxCuts) return;
        const input = this.addCut('');
        this.labelCuts();
        this.update();
        wireMotion(input.closest('.exd-wire-splice-cut'), [
          { opacity:0, transform:'scale(.9)' },
          { opacity:1, transform:'none' }
        ], { duration:260, easing:wireMotionEnter });
        input.focus();
      }, options);
      this.spliceCuts.addEventListener('input', event => {
        const input = event.target.closest('[data-wire-splice-input]');
        if (!input) return;
        const digits = input.value.replace(/\D/g, '');
        if (digits !== input.value) input.value = digits;
        this.update();
      }, options);
      this.spliceCuts.addEventListener('keydown', event => {
        if (event.key !== 'Enter' || !event.target.closest('[data-wire-splice-input]')) return;
        event.preventDefault();
        event.target.blur();
      }, options);
      this.spliceCuts.addEventListener('click', event => {
        const remove = event.target.closest('[data-wire-splice-remove]');
        if (!remove) return;
        const field = remove.parentNode;
        if (field.classList.contains('is-leaving')) return;
        const index = this.cutInputs().indexOf(field.querySelector('[data-wire-splice-input]'));
        field.classList.add('is-leaving');
        wireMotion(field, [
          { opacity:1, transform:'none' },
          { opacity:0, transform:'scale(.9)' }
        ], { duration:160, easing:wireMotionExit, fill:'forwards' }).then(() => {
          field.remove();
          this.labelCuts();
          this.update();
          const remaining = this.cutInputs();
          (remaining[Math.min(index, remaining.length - 1)] || this.spliceAdd).focus();
        });
      }, options);
      // A fresh splice group per add, so adding the same cuts again makes new lines instead of merging.
      this.add.addEventListener('click', () => {
        if (!this.splicing) return;
        this.spliceGroup = this.newSpliceGroup();
        this.update();
      }, options);
    }
    newSpliceGroup() {
      return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
    }
    feetValue(value) {
      const text = String(value || '').trim();
      if (!/^[1-9][0-9]*$/.test(text)) return 0;
      const amount = Number(text);
      return Number.isSafeInteger(amount) && amount <= 100000 ? amount : 0;
    }
    cutInputs() {
      return [...this.spliceCuts.querySelectorAll('[data-wire-splice-input]')];
    }
    // Each filled field is one cut. Empty fields are skipped; anything that isn't a whole number of
    // feet makes the order invalid until it's fixed.
    spliceLengths() {
      const lengths = [];
      let valid = true;
      this.cutInputs().forEach(input => {
        if (input.value === '') {
          input.setAttribute('aria-invalid', 'false');
          return;
        }
        const length = this.feetValue(input.value);
        input.setAttribute('aria-invalid', length ? 'false' : 'true');
        if (!length) valid = false;
        lengths.push(length);
      });
      return { lengths, valid: valid && lengths.length > 0 };
    }
    // Tab moves from cut to cut; the remove buttons stay with the mouse and touch (emptying a field
    // drops that cut too).
    addCut(value) {
      const field = document.createElement('div');
      field.className = 'exd-wire-splice-cut';
      field.innerHTML = '<label class="exd-wire-splice-field"><span class="exd-wire-splice-label"></span><input type="text" inputmode="numeric" pattern="[0-9]*" maxlength="6" autocomplete="off" data-wire-splice-input><span class="exd-wire-splice-unit" aria-hidden="true">ft</span></label><button type="button" class="exd-wire-splice-remove" data-wire-splice-remove tabindex="-1"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg></button>';
      const input = field.querySelector('input');
      input.value = value ? String(value) : '';
      this.spliceCuts.appendChild(field);
      return input;
    }
    labelCuts() {
      const inputs = this.cutInputs();
      inputs.forEach((input, index) => {
        const field = input.closest('.exd-wire-splice-cut');
        const number = String(index + 1);
        field.querySelector('.exd-wire-splice-label').textContent = this.splice.dataset.cutLabel.replace('[n]', number);
        const remove = field.querySelector('[data-wire-splice-remove]');
        remove.setAttribute('aria-label', this.splice.dataset.removeLabel.replace('[n]', number));
        remove.hidden = inputs.length <= 1;
      });
      this.spliceAdd.hidden = inputs.length >= this.maxCuts;
    }
    // The card unfolds from the Splice icon's corner, then its header, cuts and buttons rise in turn.
    revealCard() {
      wireMotion(this.splice, [
        { opacity:0, transform:'translateY(-6px) scale(.96)', clipPath:'inset(0 0 70% 0 round 12px)' },
        { opacity:1, offset:.35 },
        { opacity:1, transform:'none', clipPath:'inset(0 0 0% 0 round 12px)' }
      ], { duration:420, easing:wireMotionEnter });
      const parts = [this.splice.querySelector('.exd-wire-splice-head'), ...this.spliceCuts.children, this.splice.querySelector('.exd-wire-splice-foot')];
      parts.forEach((part, index) => {
        wireMotion(part, [
          { opacity:0, transform:'translateY(8px)' },
          { opacity:1, transform:'none' }
        ], { duration:320, delay:70 + index * 40, easing:wireMotionEnter, fill:'backwards' });
      });
    }
    foldCard() {
      return wireMotion(this.splice, [
        { opacity:1, transform:'none', clipPath:'inset(0 0 0% 0 round 12px)' },
        { opacity:0, transform:'translateY(-4px) scale(.98)', clipPath:'inset(0 0 60% 0 round 12px)' }
      ], { duration:200, easing:wireMotionExit, fill:'forwards' });
    }
    enter(element) {
      wireMotion(element, [
        { opacity:0, transform:'scale(.97)' },
        { opacity:1, transform:'none' }
      ], { duration:280, easing:wireMotionEnter });
    }
    // Splice starts as two cuts: the selected length split in half (75 ft becomes 38 + 37), so the
    // total and price don't change. Several identical cuts each get their own field.
    openSplice(event) {
      const length = this.feetValue(this.length);
      const pieces = Number.isInteger(this.cuts.valueAsNumber) && this.cuts.valueAsNumber > 1 ? this.cuts.valueAsNumber : 1;
      let values = [Math.ceil(length / 2), Math.floor(length / 2)];
      if (pieces > 1) values = Array.from({length:Math.min(pieces, this.maxCuts)}, () => length);
      this.spliceCuts.innerHTML = '';
      values.forEach(value => this.addCut(value || ''));
      this.splicing = true;
      this.labelCuts();
      this.update();
      this.revealCard();
      // Keyboard users land in the first cut; a tap or click doesn't pop up the phone keyboard.
      if (event && event.detail === 0) this.cutInputs()[0].focus();
      else this.splice.focus({preventScroll:true});
    }
    // Back to one cut: the first filled cut becomes the custom length.
    closeSplice() {
      if (this.spliceMoving) return;
      const first = this.cutInputs().map(input => this.feetValue(input.value)).find(length => length > 0);
      this.spliceMoving = true;
      this.foldCard().then(animation => {
        this.spliceMoving = false;
        this.splicing = false;
        this.spliceCuts.innerHTML = '';
        this.cuts.value = 1;
        if (first) {
          this.length = first;
          this.feet.value = first;
        }
        this.custom = true;
        this.update();
        animation?.cancel();
        this.enter(this.lengthGroup);
        this.feet.focus({preventScroll:true});
      });
    }
    // The card's Add to Cart adds the cuts (or opens the quote email), then folds the card away and
    // puts the order back the way the page loaded it. The cart popup reads the form as it submits,
    // so the reset can't change what was added. A cut that isn't a whole number keeps the card open.
    addSplice() {
      if (this.spliceMoving || this.spliceCart.disabled) return;
      const invalid = this.cutInputs().find(input => input.getAttribute('aria-invalid') === 'true');
      if (invalid) {
        invalid.focus();
        return;
      }
      this.update();
      if (!this.quote.hidden) {
        window.location.href = this.quote.href;
        return;
      }
      if (this.add.disabled) return;
      this.add.click();
      this.spliceMoving = true;
      this.foldCard().then(animation => {
        this.spliceMoving = false;
        this.splicing = false;
        this.spliceCuts.innerHTML = '';
        this.cuts.value = 1;
        this.length = this.initialLength;
        this.feet.value = this.initialLength;
        this.custom = this.initialCustom;
        this.update();
        animation?.cancel();
        this.enter(this.lengthGroup);
      });
    }
    // One cart line per cut. "Splice cut" keeps identical lengths on their own lines, and the hidden
    // group id (renewed on every add) keeps a repeat add from merging into earlier lines. Shopify
    // lists the newest line first, so the cuts are sent last to first to read Cut 1 at the top.
    spliceLines(lengths) {
      return lengths.map((length, index) => ({
        id:Number(this.form.elements.namedItem('id').value),
        quantity:length,
        properties:{
          'Cut length':`${length} ft`,
          'Number of cuts':'1',
          'Splice cut':`${index + 1} of ${lengths.length}`,
          '_Splice group':this.spliceGroup
        }
      })).reverse();
    }
    cutCountText(count) {
      return count === 1 ? this.dataset.cutOne : this.dataset.cutMany.replace('[count]', this.number.format(count));
    }
    stockState() {
      return [this.quantity.min, this.quantity.max, this.quantity.step, this.quantity.disabled].join('|');
    }
    update() {
      const spliceState = this.splicing ? this.spliceLengths() : null;
      const cuts = this.splicing ? spliceState.lengths.length : this.cuts.valueAsNumber;
      const validLength = this.splicing ? spliceState.valid : Number.isInteger(this.length) && this.length >= 1 && this.length <= 100000;
      const validCuts = Number.isInteger(cuts) && cuts >= 1 && cuts <= 999;
      const valid = validLength && validCuts;
      const supported = this.byFoot || this.length === this.reelLength && this.reelLength > 0;
      const totalFeet = !valid ? 0 : this.splicing ? spliceState.lengths.reduce((sum, length) => sum + length, 0) : this.length * cuts;
      const units = valid && supported ? this.byFoot ? totalFeet : cuts : 0;
      const minimum = Number(this.quantity.min || this.dataset.min);
      const increment = Number(this.quantity.step || this.dataset.increment);
      const limit = this.quantity.max === '' ? Number(this.dataset.limit) : Number(this.quantity.max);
      const withinRules = units >= minimum && (units - minimum) % increment === 0;
      const withinStock = this.dataset.available === 'true' && !this.quantity.disabled && (limit < 0 || units <= limit);
      let unitPrice = Number(this.dataset.price);
      this.priceBreaks.forEach(tier => { if (units >= tier.minimum) unitPrice = tier.price; });
      const available = valid && supported && withinRules && withinStock;
      const perFoot = this.byFoot ? unitPrice : this.reelLength > 0 ? unitPrice / this.reelLength : unitPrice;
      const priceLabel = this.money.format(perFoot / 100);
      const priceElement = this.querySelector('[data-unit-price]');
      priceElement.textContent = priceLabel;
      priceElement.setAttribute('aria-label', priceLabel);
      // A splice prices its total footage; a single length prices one cut.
      const pricedFeet = this.splicing ? totalFeet : this.length;
      this.querySelector('[data-wire-price-detail]').textContent = valid && supported ? `${this.money.format(unitPrice * (this.byFoot ? pricedFeet : 1) / 100)} / ${this.dataset.feet.replace('[length]',this.number.format(pricedFeet))}` : '';
      this.querySelector('[data-wire-total-feet]').textContent = valid ? this.dataset.feet.replace('[length]',this.number.format(totalFeet)) : '—';
      this.querySelector('[data-wire-total-price]').textContent = valid && supported ? this.money.format(unitPrice * units / 100) : this.dataset.quoteTotal;
      const summary = cuts === 1 ? this.dataset.summaryOne || this.dataset.summary : this.dataset.summary;
      this.querySelector('[data-wire-summary]').textContent = !valid ? '' : this.splicing
        ? `${this.cutCountText(cuts)} · ${spliceState.lengths.map(length => this.number.format(length)).join(' + ')} ft`
        : summary.replace('[length]',this.number.format(this.length)).replace('[count]',this.number.format(cuts));
      this.presets.forEach(button => button.setAttribute('aria-pressed',String(!this.custom && Number(button.dataset.wireLength) === this.length)));
      this.customButton.setAttribute('aria-pressed',String(this.custom));
      this.customButton.setAttribute('aria-expanded',String(this.custom));
      this.customField.hidden = !this.custom;
      this.feet.setAttribute('aria-invalid',String(!this.splicing && !validLength));
      this.cuts.setAttribute('aria-invalid',String(!this.splicing && !validCuts));
      this.querySelector('[data-wire-cut-length]').value = this.splicing ? spliceState.lengths.map(length => `${length} ft`).join(', ') : `${this.length} ft`;
      this.querySelector('[data-wire-cut-count]').value = String(cuts);
      this.quantity.value = available ? units : 0;
      this.quantity.dispatchEvent(new Event('change',{bubbles:true}));
      this.addDisabled = !available;
      this.quote.hidden = !valid || available;
      if (this.trade) this.trade.hidden = valid && !available;
      const inCart = this.querySelector('excel-delivery-options')?.dataset.inCart || this.dataset.cartQuantity;
      const specialOrder = this.dataset.inventoryManaged === 'true' && this.dataset.inventoryPolicy === 'continue' && units + Number(inCart) > Number(this.dataset.inventory);
      if (this.add) {
        this.add.disabled = this.addDisabled;
        this.add.hidden = valid && !available;
        this.add.classList.toggle('is-special-order',specialOrder);
        this.add.textContent = specialOrder ? this.add.dataset.specialLabel : this.add.dataset.addLabel;
      }
      if (this.splice) {
        if (this.splicing && available && cuts > 1) this.form.dataset.cartLines = JSON.stringify(this.spliceLines(spliceState.lengths));
        else delete this.form.dataset.cartLines;
        this.splice.hidden = !this.splicing;
        this.spliceToggle.setAttribute('aria-expanded', String(this.splicing));
        this.spliceSummary.textContent = this.splicing && cuts ? `${this.cutCountText(cuts)}${validLength ? ` · ${this.dataset.feet.replace('[length]', this.number.format(totalFeet))}` : ''}` : '';
        this.classList.toggle('is-splicing', this.splicing);
        // While the card is open, its own button stands in for Add to Cart (or the quote email).
        const quoteNext = !this.quote.hidden;
        this.spliceCartLabel.textContent = quoteNext ? this.quote.textContent.trim() : this.add.textContent;
        this.spliceCart.disabled = !quoteNext && this.add.disabled;
        this.spliceCart.classList.toggle('is-quote', quoteNext);
        this.spliceCart.classList.toggle('is-special-order', !quoteNext && specialOrder && !this.add.disabled);
      }
      let message = '';
      if (!validLength) message = this.dataset.invalidLength;
      else if (!validCuts) message = this.dataset.invalidCuts;
      else if (!supported) message = this.dataset.quoteLength;
      else if (!withinRules) message = this.dataset.quantityRule;
      else if (!withinStock) message = this.dataset.quoteStock;
      this.message.textContent = message;
      this.message.hidden = !message || (this.splicing && !spliceState.lengths.length);
      if (valid) {
        const lengthText = this.splicing ? spliceState.lengths.join(' + ') : String(this.length);
        const body = this.dataset.quoteBody.replace('[length]',lengthText).replace('[cuts]',String(cuts)).replace('[total]',String(totalFeet));
        this.quote.href = `mailto:${this.dataset.quoteEmail}?subject=${encodeURIComponent(this.dataset.quoteSubject)}&body=${encodeURIComponent(body)}`;
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.stockObserver?.disconnect();
      this.initialized = false;
    }
  });
}
